import React, { useState, useEffect } from 'react';
import BRAND from '../brand';
import { api, getTelegramUser, pluralize } from '../api';
import { useCatalogLabels } from '../catalogLabels';
import { EmptyStateIcon, Spinner, StatusIcon, StatusBadge, FieldIcon, SectionLoader , HeroIcon } from '../components/NavIcons';
import { useDeliverySlots } from '../deliverySlots';
import { openLink as platformOpenLink, openPlatformLink, getPlatform, closeApp } from '../platform';
import { payViaTgInvoice } from '../payments';
import PaymentTimer from '../components/PaymentTimer';
import { componentName } from '../utils/componentName';
import { TG_BOT_USERNAME } from '../utils/attribution';
import { ORDER_STATUS_LABELS } from '../orderStatusLabels';
import { fetchAppSettings } from '../settings';

// Иконка и цвет статуса — свои; подписи — orderStatusLabels.js. packaging здесь намеренно
// нет: это внутренний статус упаковки, покупателю показываем «Новый» (fallback ниже).
const STATUS_META = {
  new: { icon: 'dot', color: '#2196F3' },
  ready: { icon: 'box', color: '#FFC107' },
  courier: { icon: 'truck', color: '#9E9E9E' },
  completed: { icon: 'check', color: '#4CAF50' },
  cancelled: { icon: 'x', color: '#F44336' }
};
const statusLabels = Object.fromEntries(
  Object.entries(STATUS_META).map(([k, meta]) => [k, { ...meta, label: ORDER_STATUS_LABELS[k] || k }]),
);

const paymentStatusLabels = {
  pending: { label: 'Ожидает оплаты', icon: 'clock', color: '#FF9800' },
  paid: { label: 'Оплачен', icon: 'card', color: '#4CAF50' },
  paid_chat: { label: 'Оплачен', icon: 'card', color: '#4CAF50' },
  failed: { label: 'Ошибка оплаты', icon: 'x', color: '#F44336' },
  expired: { label: 'Истёк срок оплаты', icon: 'clock', color: '#9E9E9E' }
};

const subscriptionStatusLabels = {
  active: { label: 'Активна', icon: 'refresh', color: '#2196F3' },
  paused: { label: 'Пауза', icon: 'pause', color: '#FF9800' },
  completed: { label: 'Завершена', icon: 'check', color: '#4CAF50' },
  cancelled: { label: 'Отменена', icon: 'x', color: '#F44336' },
};

function OrdersPage() {
  const slotsConfig = useDeliverySlots();
  const catalogLabels = useCatalogLabels();
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [payingOrderId, setPayingOrderId] = useState(null);
  const [editingTimeOrderId, setEditingTimeOrderId] = useState(null);
  const [blockedSlotsForEdit, setBlockedSlotsForEdit] = useState([]);
  const [savingTime, setSavingTime] = useState(false);
  const [vaseMinDeliveries, setVaseMinDeliveries] = useState(4);
  const [showMaxSupportPopup, setShowMaxSupportPopup] = useState(false);

  const telegramUser = getTelegramUser();
  const platform = getPlatform();
  const isWebAuth = platform === 'web' && (typeof window !== 'undefined' && !!localStorage.getItem('stems_jwt'));

  const loadOrders = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      // Гость на сайте (нет ни подписи мессенджера, ни веб-токена): заказов у него
      // нет, а запросы «мои заказы/подписки/сертификаты» ушли бы без личности —
      // сервер ответит 401, и api.js перезагрузит приложение. Не спрашиваем вовсе.
      const isGuest = !telegramUser || telegramUser.platform === 'guest';
      if (isGuest && !isWebAuth) {
        setIsLoading(false);
        return;
      }
      // Сертификаты теперь standalone (отдельная таблица), тянем параллельно с
      // обычными заказами и мерджим в один список через order_type='certificate'.
      const [ordersData, subsData, certsRaw] = isWebAuth
        ? await Promise.all([
            api.getCustomerOrders(),
            api.getCustomerSubscriptions(),
            api.getMyCertificatePurchases().catch(() => []),
          ])
        : await Promise.all([
            api.getUserOrders(telegramUser.telegram_id || telegramUser.max_id),
            api.getMySubscriptions(telegramUser.telegram_id || telegramUser.max_id),
            api.getMyCertificatePurchases().catch(() => []),
          ]);
      // Привести cert к виду совместимому с обычным order для существующего
      // рендеринга (поля order.certificate.X, order.id, order.total и т.д.).
      const certsAsOrders = (certsRaw || []).map(cert => ({
        id: cert.id,
        order_type: 'certificate',
        order_number: null,
        total: cert.total || cert.amount,
        payment_status: cert.payment_status || 'pending',
        payment_url: cert.payment_url || null,
        payment_url_generated_at: cert.payment_url_generated_at || null,
        order_date: cert.order_date || cert.created_at,
        cancellation_reason: cert.cancellation_reason || null,
        certificate: cert,  // вложенный объект — рендеринг детального экрана это уже использует
      }));
      // Сортируем общий список по order_date desc (если у обычных заказов
      // нет order_date, fallback на created_at)
      const merged = [...ordersData, ...certsAsOrders].sort((a, b) => {
        const da = a.order_date || a.created_at || '';
        const db = b.order_date || b.created_at || '';
        return String(db).localeCompare(String(da));
      });
      // Не трогаем состояние, если данные не изменились: фоновый поллинг
      // (каждые 10 сек) иначе перерисовывал список вхолостую — а на iOS
      // отложенный за время шторки инвойса тик «выстреливал» ровно при её
      // закрытии, и список зримо «обновлялся» перед попапом.
      setOrders(prev => (JSON.stringify(prev) === JSON.stringify(merged) ? prev : merged));
      setSubscriptions(prev => (JSON.stringify(prev) === JSON.stringify(subsData) ? prev : subsData));
      setSelectedOrder(prev => {
        if (!prev) return null;
        const updated = merged.find(o => o.id === prev.id && (o.order_type === 'certificate') === (prev.order_type === 'certificate'));
        return updated || prev;
      });
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };


  useEffect(() => {
    loadOrders(false);
    fetchAppSettings().then(s => {
      if (s.vase_min_deliveries) setVaseMinDeliveries(Number(s.vase_min_deliveries));
    }).catch(() => {});
    const interval = setInterval(() => loadOrders(true), 10000);
    return () => clearInterval(interval);
    // Одноразовая настройка polling при маунте
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedOrder) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedOrder]);

  const handlePay = async (order, e) => {
    e.stopPropagation();
    // Защита от двойных нажатий: пока создаётся инвойс/ссылка и открывается
    // шторка Telegram, кнопка в «Открываем оплату…» и заблокирована.
    if (payingOrderId) return;
    setPayingOrderId(order.id);
    const isCert = order.order_type === 'certificate';
    // Cert использует отдельный маркер pendings: id-пространства Order и
    // GiftCertificate пересекаются, без маркера App.jsx при возврате тянет
    // /payment/status/{id} и находит постороннего Order, отсюда ложный тост.
    if (isCert) {
      localStorage.setItem('stems_pending_payment_cert', order.id.toString());
      localStorage.removeItem('stems_pending_payment');
    } else {
      localStorage.setItem('stems_pending_payment', order.id.toString());
      localStorage.removeItem('stems_pending_payment_cert');
    }
    // Нативный Telegram-инвойс — единый путь для заказов и сертификатов
    // (payments.js). false → redirect-флоу ниже (маркеры pending остаются).
    if (await payViaTgInvoice({
      id: order.id,
      kind: isCert ? 'cert' : 'order',
      orderNumber: order.order_number,
      total: order.total,
      onBusy: (busy) => setPayingOrderId(busy ? order.id : null),
    })) return;
    // openLink — сразу, до await (TG WebApp.openLink требует user-gesture
    // контекст; долгий await ломает на TG Desktop Mac).
    if (order.payment_url) {
      platformOpenLink(order.payment_url);
      setPayingOrderId(null);
      return;
    }
    try {
      const result = isCert
        ? await api.getCertificatePaymentUrl(order.id)
        : await api.getOrderPaymentUrl(order.id);
      if (result?.payment_url) {
        platformOpenLink(result.payment_url);
      } else {
        alert('Не удалось создать платёж. Попробуйте ещё раз через минуту.');
      }
    } catch (err) {
      alert(err?.message || 'Не удалось создать платёж. Попробуйте ещё раз через минуту.');
    } finally {
      setPayingOrderId(null);
    }
  };

  const handleOrderQuestion = (order, e) => {
    e.stopPropagation();
    setShowMaxSupportPopup(true);
  };

  if (!telegramUser) {
    return (
      <div className="page-container" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ marginBottom: '16px' }}><HeroIcon name="lock" size={56} /></div>
        <h2 style={{ marginBottom: '8px', fontSize: '20px' }}>Доступно только в Telegram</h2>
        <p style={{ color: '#666', fontSize: '14px' }}>
          Для просмотра заказов откройте приложение через бот @{TG_BOT_USERNAME} в Telegram
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page-container">
        <SectionLoader />
      </div>
    );
  }

  if (orders.length === 0 && subscriptions.length === 0) {
    // Геометрия 1:1 с пустой корзиной (.empty-cart-box) — иконка и заголовок
    // на одном уровне, переключение вкладок без «дёрганья»
    return (
      <div className="page-container" style={{ padding: '40px 24px' }}>
        <div style={{
          background: 'var(--card-bg)',
          borderRadius: '24px',
          padding: '40px 32px',
          maxWidth: '320px',
          margin: '0 auto',
          textAlign: 'center',
        }}>
          <div style={{ marginBottom: '16px' }}><EmptyStateIcon name="orders" size={56} /></div>
          <h2 style={{ marginBottom: '8px', fontSize: '20px' }}>У вас пока нет заказов</h2>
          <p style={{ color: '#666', fontSize: '14px' }}>
            Оформите первый заказ в каталоге
          </p>
        </div>
      </div>
    );
  }

  // Фильтруем обычные заказы (не subscription), подписки показываем отдельно
  const regularOrders = orders.filter(o => o.order_type !== 'subscription');

  // Объединённый список отсортированный по order_number убыванию
  const allItems = [
    ...regularOrders.map(o => ({ ...o, _type: 'order' })),
    ...subscriptions.map(s => ({ ...s, _type: 'subscription' })),
  ].sort((a, b) => {
    const da = a.order_date || a.created_at || '';
    const db = b.order_date || b.created_at || '';
    return String(db).localeCompare(String(da));
  });

  const ALL_TIME_SLOTS = slotsConfig.timeSlots.map(value => ({
    value,
    label: value.replace('-', ' – '),
  }));

  const canChangeTime = (deliveryDate, status) => {
    if (status === 'completed' || status === 'cancelled') return false;
    if (!deliveryDate) return false;
    const [y, m, d] = deliveryDate.split('-').map(Number);
    const deadline = new Date(y, m - 1, d - 1, 19, 0, 0);
    return new Date() < deadline;
  };

  const handleEditTime = async (del) => {
    if (editingTimeOrderId === del.id) {
      setEditingTimeOrderId(null);
      return;
    }
    setEditingTimeOrderId(del.id);
    try {
      const result = await api.getBlockedSlotsForDate(del.delivery_date);
      setBlockedSlotsForEdit(result.blocked === 'all' ? ALL_TIME_SLOTS.map(s => s.value) : (result.blocked || []));
    } catch (e) {
      setBlockedSlotsForEdit([]);
    }
  };

  const handleSelectTime = async (del, timeValue) => {
    if (savingTime) return;
    setSavingTime(true);
    try {
      const updated = await api.updateSubscriptionDeliveryTime(
        selectedSubscription.id, del.id, timeValue, telegramUser?.telegram_id || telegramUser?.max_id || ''
      );
      setSelectedSubscription(updated);
      setEditingTimeOrderId(null);
    } catch (e) {
      console.error('Failed to update delivery time:', e);
      alert(e.message || 'Не удалось изменить время');
    } finally {
      setSavingTime(false);
    }
  };

  // Детали подписки
  if (selectedSubscription) {
    const subStatus = subscriptionStatusLabels[selectedSubscription.status] || subscriptionStatusLabels.active;
    const subPayment = paymentStatusLabels[selectedSubscription.payment_status] || paymentStatusLabels.pending;
    return (
      <div className="page-container" style={{ padding: '16px' }}>
        <button
          onClick={() => setSelectedSubscription(null)}
          style={{ background: 'white', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '8px 16px', marginBottom: '16px', cursor: 'pointer', fontSize: '14px' }}
        >
          ← Назад к заказам
        </button>

        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px' }}>
          <h2 style={{ marginBottom: '4px', fontSize: '20px' }}>Подписка #{selectedSubscription.order_number}</h2>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>{selectedSubscription.catalog_name}</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Статус</div>
              <div style={{ fontSize: '15px', fontWeight: '500', color: subStatus.color }}>
                <StatusIcon name={subStatus.icon} />{subStatus.label}
              </div>
            </div>
            <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Оплата</div>
              <div style={{ fontSize: '15px', fontWeight: '500', color: subPayment.color }}>
                <StatusIcon name={subPayment.icon} />{subPayment.label}
              </div>
            </div>
          </div>

          <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px', marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Доставки</div>
            <div style={{ fontSize: '16px', fontWeight: '500' }}>
              {selectedSubscription.completed_deliveries} из {selectedSubscription.total_deliveries}
            </div>
          </div>

          {selectedSubscription.delivery_address && (
            <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px', marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Адрес доставки</div>
              <div style={{ fontSize: '14px' }}>
                {selectedSubscription.delivery_address}
                {selectedSubscription.apartment && `, кв. ${selectedSubscription.apartment}`}
              </div>
            </div>
          )}

          <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: '#666', fontSize: '14px' }}>Стоимость</span>
              <span style={{ fontSize: '14px' }}>{selectedSubscription.subtotal?.toLocaleString('ru-RU')} ₽</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: '#666', fontSize: '14px' }}>Сервисный сбор</span>
              <span style={{ fontSize: '14px' }}>{selectedSubscription.service_fee?.toLocaleString('ru-RU')} ₽</span>
            </div>
            {selectedSubscription.discount_amount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#E65100', fontSize: '14px' }}>Скидка {selectedSubscription.discount_percent}%</span>
                <span style={{ color: '#E65100', fontSize: '14px' }}>−{selectedSubscription.discount_amount?.toLocaleString('ru-RU')} ₽</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: '#666', fontSize: '14px' }}>Доставка</span>
              {(selectedSubscription.delivery_cost || 0) > 0 ? (
                <span style={{ color: '#666', fontSize: '14px' }}>{selectedSubscription.delivery_cost.toLocaleString('ru-RU')} ₽</span>
              ) : (
                <span style={{ color: '#4CAF50', fontSize: '14px' }}>Бесплатно</span>
              )}
            </div>
            {selectedSubscription.total_deliveries >= vaseMinDeliveries && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#666', fontSize: '14px' }}>Замена вазы</span>
                <span style={{ color: '#4CAF50', fontSize: '14px' }}>Бесплатно</span>
              </div>
            )}
            {selectedSubscription.bonus_used > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#666', fontSize: '14px' }}>Со счёта {BRAND.name}</span>
                <span style={{ color: '#4CAF50', fontSize: '14px' }}>−{selectedSubscription.bonus_used} ₽</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '2px solid #000', fontWeight: '700', fontSize: '16px' }}>
              <span>Итого</span>
              <span>{selectedSubscription.total?.toLocaleString('ru-RU')} ₽</span>
            </div>
          </div>
        </div>

        {/* Список доставок */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px' }}>
          <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>Доставки</h3>
          <div style={{ display: 'grid', gap: '8px' }}>
            {selectedSubscription.orders?.map((del) => {
              const delStatus = statusLabels[del.status] || statusLabels.new;
              const delTime = del.delivery_time || selectedSubscription.delivery_time;
              const canEdit = canChangeTime(del.delivery_date, del.status);
              const isEditing = editingTimeOrderId === del.id;
              const availableSlots = ALL_TIME_SLOTS.filter(s => !blockedSlotsForEdit.includes(s.value));
              return (
                <div key={del.id} style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '14px' }}>Доставка {del.delivery_number}</div>
                      <div style={{ fontSize: '13px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {del.delivery_date ? del.delivery_date.split('-').reverse().join('.') : '—'}
                        {delTime && `, ${delTime}`}
                        {canEdit && (
                          <span
                            onClick={() => handleEditTime(del)}
                            title="Изменить время доставки"
                            style={{ cursor: 'pointer', display: 'inline-flex', color: isEditing ? '#2196F3' : '#999' }}
                          >
                            <FieldIcon name="edit" size={13} />
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <StatusBadge icon={delStatus.icon} label={delStatus.label} color={delStatus.color} />
                    </div>
                  </div>
                  {isEditing && (
                    <div
                      style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #E0E0E0' }}
                    >
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '6px' }}>Выберите время:</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                        {availableSlots.map(slot => (
                          <div
                            key={slot.value}
                            onClick={() => !savingTime && handleSelectTime(del, slot.value)}
                            style={{
                              padding: '8px',
                              fontSize: '13px',
                              textAlign: 'center',
                              borderRadius: '6px',
                              cursor: savingTime ? 'not-allowed' : 'pointer',
                              background: delTime === slot.value ? '#2196F3' : '#fff',
                              color: delTime === slot.value ? '#fff' : '#333',
                              border: '1px solid ' + (delTime === slot.value ? '#2196F3' : '#ddd'),
                              fontWeight: delTime === slot.value ? '600' : '400',
                              opacity: savingTime ? 0.6 : 1,
                            }}
                          >
                            {slot.label}
                          </div>
                        ))}
                      </div>
                      {availableSlots.length === 0 && (
                        <div style={{ fontSize: '13px', color: '#999', textAlign: 'center', padding: '8px' }}>
                          Нет доступных слотов
                        </div>
                      )}
                    </div>
                  )}
                  {del.items && del.items.length > 0 && (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #E0E0E0' }}>
                      {del.items.map((item, idx) => (
                        <div key={idx} style={{ fontSize: '13px', color: '#333', padding: '2px 0' }}>
                          {item.name} — {item.quantity} шт
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {selectedSubscription.payment_status === 'pending' && selectedSubscription.payment_type === 'card' && (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              if (payingOrderId) return;
              // Нативный Telegram-инвойс (payments.js); false → redirect ниже
              localStorage.setItem('stems_pending_subscription', JSON.stringify({
                subscription_id: selectedSubscription.id, timestamp: Date.now(),
              }));
              if (await payViaTgInvoice({
                id: selectedSubscription.id,
                kind: 'sub',
                orderNumber: selectedSubscription.order_number,
                total: selectedSubscription.total,
                onBusy: (busy) => setPayingOrderId(busy ? selectedSubscription.id : null),
              })) return;
              if (selectedSubscription.payment_url) {
                platformOpenLink(selectedSubscription.payment_url);
                setPayingOrderId(null);
              } else {
                try {
                  const r = await api.getSubscriptionPaymentUrl(selectedSubscription.id);
                  if (r?.payment_url) platformOpenLink(r.payment_url);
                  else alert('Не удалось создать платёж. Попробуйте через минуту.');
                } catch (err) {
                  alert(err?.message || 'Не удалось создать платёж. Попробуйте через минуту.');
                } finally {
                  setPayingOrderId(null);
                }
              }
            }}
            style={{
              marginTop: '16px', width: '100%', padding: '16px',
              background: payingOrderId === selectedSubscription.id ? '#CCC' : '#4CAF50',
              color: 'white', border: 'none', borderRadius: '12px',
              fontSize: '16px', fontWeight: '700',
              cursor: payingOrderId === selectedSubscription.id ? 'not-allowed' : 'pointer',
            }}
          >
            {payingOrderId === selectedSubscription.id ? 'Загружаем…' : (
              <>Оплатить<PaymentTimer generatedAt={selectedSubscription.payment_url_generated_at} /></>
            )}
          </button>
        )}

      </div>
    );
  }

  if (selectedOrder && selectedOrder.order_type === 'certificate') {
    const order = selectedOrder;
    const cert = order.certificate;
    const certPayLabel = paymentStatusLabels[order.payment_status] || paymentStatusLabels.pending;
    // Cert-status badge показываем только когда оплата прошла (lifecycle сертификата
    // начинается с момента оплаты). До оплаты — единственный бейдж: payment_status.
    const isCertPaid = order.payment_status === 'paid' || order.payment_status === 'paid_chat';
    const certStatusLabel = isCertPaid ? (
      cert?.status === 'activated' ? { label: 'Активирован', icon: 'check', color: '#1A73E8' }
      : cert?.status === 'expired' ? { label: 'Истёк срок активации', icon: 'clock', color: '#9E9E9E' }
      : cert?.status === 'cancelled' ? { label: 'Отменён', icon: 'x', color: '#C62828' }
      : cert?.status === 'active' ? { label: 'Активен', icon: 'star', color: '#2E7D32' }
      : null
    ) : null;
    const formatDate = (iso) => {
      if (!iso) return '';
      try { return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return ''; }
    };
    const handleCopyCode = () => {
      if (cert?.code && navigator.clipboard) {
        navigator.clipboard.writeText(cert.code).catch(() => {});
      }
    };
    const showCode = cert?.code && isCertPaid && cert.status !== 'cancelled';

    return (
      <div className="page-container" style={{ padding: '16px' }}>
        <button
          onClick={() => setSelectedOrder(null)}
          style={{
            background: 'white',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            padding: '8px 16px',
            marginBottom: '16px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ← Назад к заказам
        </button>

        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
            <h2 style={{ margin: 0, fontSize: '20px' }}>
              <StatusIcon name="gift" size={18} />Сертификат{cert?.cert_number ? ` #${cert.cert_number}` : ''}
            </h2>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>
              {order.total?.toLocaleString('ru-RU')} ₽
            </div>
          </div>
          {order.order_date && (
            <div style={{ fontSize: '13px', color: '#999', marginBottom: '16px' }}>
              Куплен {formatDate(order.order_date)}
            </div>
          )}

          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: certStatusLabel ? '1fr 1fr' : '1fr', gap: '12px' }}>
              <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Статус оплаты</div>
                <div style={{ fontSize: '15px', fontWeight: '500', color: certPayLabel.color }}>
                  <StatusIcon name={certPayLabel.icon} />{certPayLabel.label}
                </div>
              </div>
              {certStatusLabel && (
                <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Статус сертификата</div>
                  <div style={{ fontSize: '15px', fontWeight: '500', color: certStatusLabel.color }}>
                    <StatusIcon name={certStatusLabel.icon} />{certStatusLabel.label}
                  </div>
                </div>
              )}
            </div>

            {(cert?.recipient_name || cert?.sender_name || cert?.message) && (
              <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>Описание</div>
                {cert.sender_name && <div style={{ fontSize: '15px', marginBottom: '4px' }}>От: <b>{cert.sender_name}</b></div>}
                {cert.recipient_name && <div style={{ fontSize: '15px', marginBottom: '4px' }}>Для: <b>{cert.recipient_name}</b></div>}
                {cert.message && (
                  <div style={{ fontSize: '14px', color: '#5D4037', fontStyle: 'italic', marginTop: '6px', lineHeight: 1.5, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                    «{cert.message}»
                  </div>
                )}
              </div>
            )}

            {cert?.expires_at && (cert.status === 'active' || cert.status === 'activated' || cert.status === 'expired') && (
              <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Срок действия</div>
                <div style={{ fontSize: '16px', fontWeight: '500' }}>
                  {cert.status === 'expired'
                    ? <>Истёк {formatDate(cert.expires_at)}</>
                    : cert.status === 'activated'
                    ? <>Активирован {formatDate(cert.activated_at)}</>
                    : <>Действует до {formatDate(cert.expires_at)}</>}
                </div>
              </div>
            )}

            {showCode && (
              <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Код активации</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <code
                    onClick={handleCopyCode}
                    title="Нажмите чтобы скопировать"
                    style={{
                      fontSize: '18px', letterSpacing: 1, fontWeight: 700, color: '#1A1A1A',
                      cursor: 'pointer',
                      WebkitUserSelect: 'all', userSelect: 'all',
                    }}
                  >
                    {cert.code}
                  </code>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={handleCopyCode}
                    style={{ padding: '8px 14px', fontSize: '13px', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    Скопировать
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {cert?.image_url && isCertPaid && cert.status !== 'activated' && (
          <button
            type="button"
            onClick={async () => {
              try {
                await api.resendCertificate(order.id);
                alert('Сертификат отправлен в чат бота — откройте чат и сохраните файл.');
              } catch (e) {
                alert(e?.message || 'Не удалось отправить сертификат в чат');
              }
            }}
            style={{
              width: '100%',
              padding: '12px 16px',
              marginBottom: '12px',
              background: '#fff',
              color: 'var(--text-color)',
              border: '1.5px solid var(--text-color)',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <StatusIcon name="gift" size={15} />Скачать сертификат
          </button>
        )}

        {order.payment_status === 'pending' && (
          <button
            onClick={(e) => handlePay(order, e)}
            disabled={payingOrderId === order.id}
            style={{
              width: '100%', padding: '14px',
              background: payingOrderId === order.id ? '#CCC' : '#F57C00', color: '#FFF', border: 'none',
              borderRadius: '12px', fontWeight: 700, fontSize: '15px',
              cursor: payingOrderId === order.id ? 'not-allowed' : 'pointer',
            }}
          >
            {payingOrderId === order.id ? 'Открываем оплату…' : (
              <>Оплатить<PaymentTimer generatedAt={order.payment_url_generated_at || order.order_date} /></>
            )}
          </button>
        )}
      </div>
    );
  }

  if (selectedOrder) {
    const statusInfo = statusLabels[selectedOrder.status] || statusLabels.new;
    const paymentInfo = paymentStatusLabels[selectedOrder.payment_status] || paymentStatusLabels.pending;

    return (
      <div className="page-container" style={{ padding: '16px' }}>
        <button
          onClick={() => setSelectedOrder(null)}
          style={{
            background: 'white',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            padding: '8px 16px',
            marginBottom: '16px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ← Назад к заказам
        </button>

        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px' }}>
          <h2 style={{ marginBottom: '4px', fontSize: '20px' }}>Заказ #{selectedOrder.order_number || selectedOrder.id}</h2>
          {selectedOrder.created_at && (
            <div style={{ fontSize: '13px', color: '#999', marginBottom: '16px' }}>
              Время заказа: {new Date(selectedOrder.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}

          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: selectedOrder.cancellation_reason === 'manual' ? '1fr' : '1fr 1fr', gap: '12px' }}>
              <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Статус заказа</div>
                <div style={{ fontSize: '15px', fontWeight: '500', color: statusInfo.color }}>
                  <StatusIcon name={statusInfo.icon} />{statusInfo.label}
                </div>
              </div>
              {selectedOrder.cancellation_reason !== 'manual' && (
                <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Статус оплаты</div>
                  <div style={{ fontSize: '15px', fontWeight: '500', color: paymentInfo.color }}>
                    <StatusIcon name={paymentInfo.icon} />{paymentInfo.label}
                  </div>
                </div>
              )}
            </div>

            <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{selectedOrder.delivery_type === 'pickup' ? 'Дата самовывоза' : 'Дата доставки'}</div>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                {selectedOrder.delivery_date ? selectedOrder.delivery_date.split('-').reverse().join('.') : '—'}
                {selectedOrder.delivery_time ? `, ${selectedOrder.delivery_time}` : ''}
              </div>
            </div>

            <div style={{ padding: '12px', background: '#F5F5F5', borderRadius: '8px' }}>
              {selectedOrder.delivery_type === 'pickup' ? (
                <>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Самовывоз</div>
                  <div style={{ fontSize: '14px' }}>{selectedOrder.pickup_address || selectedOrder.pickup_point?.address || 'адрес уточните у магазина'}</div>
                </>
              ) : selectedOrder.hide_address_from_client ? (
                /* Адрес уточнили у получателя и скрыли от заказчика — сервер его не присылает */
                <>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Получение</div>
                  <div style={{ fontSize: '14px' }}>Доставка</div>
                </>
              ) : selectedOrder.delivery_address ? (
                <>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Адрес доставки</div>
                  <div style={{ fontSize: '14px' }}>{selectedOrder.delivery_address}{selectedOrder.apartment ? `, кв. ${selectedOrder.apartment}` : ''}</div>
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '16px', padding: '20px' }}>
          <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>Товары</h3>
          {(() => {
            const allItems = selectedOrder.items || [];
            const bouquetItems = allItems.filter(it => it.bouquet_id);
            const stemItems = allItems.filter(it => !it.bouquet_id && it.assembly_sequence_no == null);
            // Группируем стебли сборных букетов по sequence_no
            const asmMap = {};
            allItems.forEach(it => {
              if (it.assembly_sequence_no != null) {
                const seq = it.assembly_sequence_no;
                if (!asmMap[seq]) asmMap[seq] = { sequence_no: seq, items: [] };
                asmMap[seq].items.push(it);
              }
            });
            const customAssemblies = Object.values(asmMap).sort((a, b) => a.sequence_no - b.sequence_no);
            const renderItem = (item, index) => (
              <div key={index} style={{
                display: 'flex', justifyContent: 'space-between', gap: 12,
                padding: '12px', background: '#F5F5F5', borderRadius: '8px',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '500', marginBottom: '4px' }}>{item.product_name}</div>
                  {/* Букет = отдельная строка по 1 шт → «1 шт × цена» дублирует итог справа, скрываем.
                      Для стеблей и кратных позиций «N шт × цена» осмысленно — показываем. */}
                  {!(item.bouquet_id && (item.quantity || 1) <= 1) && (
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      {item.quantity} шт × {item.price?.toLocaleString('ru-RU')} ₽
                    </div>
                  )}
                  {item.bouquet_components?.length > 0 && (() => {
                    // Цену букета (в т.ч. «свою цену») распределяем по компонентам
                    // пропорционально их сырой стоимости → сумма состава = цене букета.
                    // Для динамической цены scale=1 (сумма компонентов и так = цене).
                    const rawSum = item.bouquet_components.reduce(
                      (s, c) => s + (c.product_price || 0) * (c.quantity || 0), 0);
                    const scale = rawSum > 0 ? (item.price || 0) / rawSum : 1;
                    return (
                      <div style={{ marginTop: 6, paddingLeft: 10, borderLeft: '2px solid #E0E0E0', display: 'grid', gap: 2 }}>
                        {item.bouquet_components.map((c, ci) => (
                          <div key={ci} style={{
                            fontSize: '12px', color: '#888', display: 'flex',
                            justifyContent: 'space-between', gap: 10,
                          }}>
                            <span style={{ minWidth: 0 }}>{c.is_replacement ? '≠ ' : ''}{componentName(c)} × {c.quantity}</span>
                            <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{Math.round((c.product_price || 0) * (c.quantity || 0) * scale).toLocaleString('ru-RU')} ₽</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                <div style={{ fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {(item.quantity * item.price).toLocaleString('ru-RU')} ₽
                </div>
              </div>
            );
            const sectionTitleStyle = {
              fontSize: 12, fontWeight: 700, color: '#6B6B68',
              textTransform: 'uppercase', letterSpacing: 0.5, margin: '4px 0 6px',
            };
            return (
              <>
                {bouquetItems.length > 0 && (
                  <>
                    <div style={sectionTitleStyle}>{catalogLabels.bouquets}</div>
                    <div style={{ display: 'grid', gap: '12px', marginBottom: (customAssemblies.length > 0 || stemItems.length > 0) ? 14 : 0 }}>
                      {bouquetItems.map((it, i) => renderItem(it, `b-${i}`))}
                    </div>
                  </>
                )}
                {customAssemblies.length > 0 && (
                  <>
                    <div style={sectionTitleStyle}>Сборные букеты</div>
                    <div style={{ display: 'grid', gap: '12px', marginBottom: stemItems.length > 0 ? 14 : 0 }}>
                      {customAssemblies.map(asm => (
                        <div key={`asm-${asm.sequence_no}`} style={{
                          padding: '12px', background: '#F5F5F5', borderRadius: '8px',
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 6, color: '#4A148C' }}>
                            Сборный букет {asm.sequence_no}
                          </div>
                          {asm.items.map((s, i) => (
                            <div key={i} style={{
                              display: 'flex', justifyContent: 'space-between',
                              fontSize: 13, padding: '2px 0',
                            }}>
                              <span>{s.product_name} × {s.quantity}</span>
                              <span style={{ color: '#666' }}>
                                {(s.quantity * s.price).toLocaleString('ru-RU')} ₽
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {stemItems.length > 0 && (
                  <>
                    <div style={sectionTitleStyle}>Стебли</div>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {stemItems.map((it, i) => renderItem(it, `s-${i}`))}
                    </div>
                    {/* Нечётное количество относится только к стеблям. */}
                    {selectedOrder.odd_quantity && (
                      <div style={{ marginTop: '12px', padding: '8px 12px', background: '#FFF8E1', borderRadius: '8px', fontSize: '13px', color: '#5D4037', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Нечётное количество
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}

          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E0E0E0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Сумма товаров</span>
              <span>{selectedOrder.subtotal?.toLocaleString('ru-RU')} ₽</span>
            </div>
            {selectedOrder.postcard_price > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Открытка</span>
                <span>{selectedOrder.postcard_price?.toLocaleString('ru-RU')} ₽</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Сервисный сбор</span>
              <span>{selectedOrder.service_fee?.toLocaleString('ru-RU')} ₽</span>
            </div>
            {/* При самовывозе строки «Доставка 0 ₽» нет — путала покупателя */}
            {(selectedOrder.delivery_type !== 'pickup' || (selectedOrder.delivery_cost || 0) > 0) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Доставка</span>
                <span>{(selectedOrder.delivery_cost || 0).toLocaleString('ru-RU')} ₽</span>
              </div>
            )}
            {selectedOrder.bonus_used > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Со счёта {BRAND.name}</span>
                <span style={{ color: '#4CAF50' }}>−{selectedOrder.bonus_used} ₽</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', paddingTop: '12px', borderTop: '2px solid #000', fontSize: '18px', fontWeight: '700' }}>
              <span>Итого</span>
              {/* Итог берём с сервера, а не пересчитываем: у заказов подписки
                  subtotal хранит полную цену, а скидка вычитается отдельно —
                  пересчёт здесь завышал сумму ровно на скидку, при том что в
                  списке заказов строкой выше стоит правильное число. */}
              <span>{(selectedOrder.total || 0).toLocaleString('ru-RU')} ₽</span>
            </div>
          </div>
        </div>

        {selectedOrder.payment_status === 'pending' && selectedOrder.status !== 'cancelled' && (
          <button
            onClick={(e) => handlePay(selectedOrder, e)}
            disabled={payingOrderId === selectedOrder.id}
            style={{
              marginTop: '16px',
              width: '100%',
              padding: '16px',
              background: payingOrderId === selectedOrder.id ? '#CCC' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: payingOrderId === selectedOrder.id ? 'not-allowed' : 'pointer',
            }}
          >
            {payingOrderId === selectedOrder.id ? 'Загружаем…' : (
              <>Оплатить<PaymentTimer generatedAt={selectedOrder.payment_url_generated_at || selectedOrder.order_date} /></>
            )}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="page-container" style={{ padding: '16px' }}>
      <h1 style={{ marginBottom: '20px', fontSize: '24px' }}>Мои заказы</h1>

      <div style={{ display: 'grid', gap: '12px' }}>
        {allItems.map((item) => {
          if (item._type === 'subscription') {
          const sub = item;
          const subStatus = subscriptionStatusLabels[sub.status] || subscriptionStatusLabels.active;
          const subPayment = paymentStatusLabels[sub.payment_status] || paymentStatusLabels.pending;
          return (
            <div
              key={`sub-${sub.id}`}
              onClick={() => setSelectedSubscription(sub)}
              style={{
                background: 'white', borderRadius: '12px', padding: '16px', cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)', transition: 'transform 0.2s',
              }}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '600' }}>Подписка #{sub.order_number}</div>
                  <div style={{ fontSize: '13px', color: '#666' }}>{sub.catalog_name}</div>
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>
                  {sub.total?.toLocaleString('ru-RU')} ₽
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <StatusBadge icon={subStatus.icon} label={subStatus.label} color={subStatus.color} />
                    <StatusBadge icon={subPayment.icon} label={subPayment.label} color={subPayment.color} />
                    <StatusBadge icon="truck" color="#1565C0" label={`${sub.completed_deliveries}/${sub.total_deliveries} ${pluralize(sub.total_deliveries, 'доставка', 'доставки', 'доставок')}`} />
                  </div>
                </div>
                {sub.status !== 'completed' && sub.status !== 'cancelled' && (
                  <button
                    onClick={(e) => handleOrderQuestion(null, e)}
                    style={{
                      width: '36px', height: '36px', minWidth: '36px',
                      marginLeft: '8px', padding: 0,
                      background: 'white', color: '#999',
                      border: '1px solid #E0E0E0', borderRadius: '8px',
                      fontSize: '18px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    title="Вопрос по подписке"
                  >
                    <FieldIcon name="chat" size={17} />
                  </button>
                )}
              </div>
              {sub.payment_status === 'pending' && sub.payment_type === 'card' && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (payingOrderId) return;
                    // Нативный Telegram-инвойс (payments.js); false → redirect ниже
                    localStorage.setItem('stems_pending_subscription', JSON.stringify({ subscription_id: sub.id, timestamp: Date.now() }));
                    if (await payViaTgInvoice({
                      id: sub.id,
                      kind: 'sub',
                      orderNumber: sub.order_number,
                      total: sub.total,
                      onBusy: (busy) => setPayingOrderId(busy ? sub.id : null),
                    })) return;
                    if (sub.payment_url) {
                      platformOpenLink(sub.payment_url);
                      setPayingOrderId(null);
                      return;
                    }
                    try {
                      const r = await api.getSubscriptionPaymentUrl(sub.id);
                      if (r?.payment_url) platformOpenLink(r.payment_url);
                      else alert('Не удалось создать платёж. Попробуйте через минуту.');
                    } catch (err) {
                      alert(err?.message || 'Не удалось создать платёж. Попробуйте через минуту.');
                    } finally {
                      setPayingOrderId(null);
                    }
                  }}
                  style={{
                    marginTop: '12px', width: '100%', padding: '12px',
                    background: payingOrderId === sub.id ? '#CCC' : '#4CAF50',
                    color: 'white', border: 'none', borderRadius: '8px',
                    fontSize: '15px', fontWeight: '600',
                    cursor: payingOrderId === sub.id ? 'not-allowed' : 'pointer',
                  }}
                >
                  {payingOrderId === sub.id ? 'Загружаем…' : (
                    <>Оплатить<PaymentTimer generatedAt={sub.payment_url_generated_at} /></>
                  )}
                </button>
              )}
            </div>
          );
          } else {
          const order = item;
          const statusInfo = statusLabels[order.status] || statusLabels.new;
          const paymentInfo = paymentStatusLabels[order.payment_status] || paymentStatusLabels.pending;

          // Компактная карточка для подарочного сертификата — детали в selectedOrder-экране.
          if (order.order_type === 'certificate') {
            const certPayLabel = paymentStatusLabels[order.payment_status] || paymentStatusLabels.pending;
            const cert = order.certificate;
            // Cert-status badge показываем только когда оплата прошла (paid или paid_chat).
            // Системная отмена из-за просрочки оплаты отображается только в payment-бейдже
            // («Истёк срок оплаты»), без второго бейджа «Отменён».
            const isCertPaidCard = order.payment_status === 'paid' || order.payment_status === 'paid_chat';
            const certStatusLabel = isCertPaidCard ? (
              cert?.status === 'activated' ? { text: 'Активирован', color: '#1A73E8' }
              : cert?.status === 'expired' ? { text: 'Истёк срок активации', color: '#9E9E9E' }
              : cert?.status === 'cancelled' ? { text: 'Отменён', color: '#C62828' }
              : cert?.status === 'active' ? { text: 'Активен', color: '#2E7D32' }
              : null
            ) : null;
            const showCertSupport = order.payment_status === 'pending' || (isCertPaidCard && cert?.status === 'active');
            const formatDate = (iso) => {
              if (!iso) return '';
              try { return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return ''; }
            };
            return (
              <div
                key={`cert-${order.id}`}
                onClick={() => setSelectedOrder(order)}
                style={{
                  background: 'white',
                  borderRadius: 12,
                  padding: 16,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                }}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>
                    <StatusIcon name="gift" size={15} />Сертификат{cert?.cert_number ? ` #${cert.cert_number}` : ''}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>
                    {order.total?.toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                {/* Кнопка поддержки — внизу справа, как у заказов и подписок */}
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                      <StatusBadge icon={certPayLabel.icon} label={certPayLabel.label} color={certPayLabel.color} />
                      {certStatusLabel && (
                        <StatusBadge label={certStatusLabel.text} color={certStatusLabel.color} />
                      )}
                    </div>
                    {cert?.expires_at && (cert.status === 'active' || cert.status === 'activated') && (
                      <div style={{ fontSize: 13, color: '#666' }}>
                        Действует до {formatDate(cert.expires_at)}
                      </div>
                    )}
                  </div>
                  {showCertSupport && (
                    <button
                      onClick={(e) => handleOrderQuestion(order, e)}
                      style={{
                        width: '36px', height: '36px', minWidth: '36px',
                        marginLeft: '8px', padding: 0,
                        background: 'white', color: '#999',
                        border: '1px solid #E0E0E0', borderRadius: '8px',
                        fontSize: '18px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      title="Вопрос по сертификату"
                    >
                      <FieldIcon name="chat" size={17} />
                    </button>
                  )}
                </div>

                {order.payment_status === 'pending' && (
                  <button
                    onClick={(e) => handlePay(order, e)}
                    disabled={payingOrderId === order.id}
                    style={{
                      width: '100%', marginTop: 12, padding: 12,
                      background: payingOrderId === order.id ? '#CCC' : '#F57C00', color: '#FFF', border: 'none',
                      borderRadius: 10, fontWeight: 700, fontSize: 15,
                      cursor: payingOrderId === order.id ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {payingOrderId === order.id ? 'Открываем оплату…' : (
                      <>Оплатить<PaymentTimer generatedAt={order.payment_url_generated_at || order.order_date} /></>
                    )}
                  </button>
                )}
              </div>
            );
          }

          return (
            <div
              key={`order-${order.id}`}
              onClick={() => setSelectedOrder(order)}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '16px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                transition: 'transform 0.2s',
              }}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>Заказ #{order.order_number || order.id}</div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>
                  {order.total?.toLocaleString('ru-RU')} ₽
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <StatusBadge icon={statusInfo.icon} label={statusInfo.label} color={statusInfo.color} />
                    {order.cancellation_reason !== 'manual' && (
                      <StatusBadge icon={paymentInfo.icon} label={paymentInfo.label} color={paymentInfo.color} />
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    {order.delivery_type === 'pickup' ? 'Самовывоз' : 'Доставка'}: {order.delivery_date ? order.delivery_date.split('-').reverse().join('.') : '—'} {order.delivery_time || ''}
                  </div>
                </div>
                {order.status !== 'completed' && order.status !== 'cancelled' && (
                  <button
                    onClick={(e) => handleOrderQuestion(order, e)}
                    style={{
                      width: '36px',
                      height: '36px',
                      minWidth: '36px',
                      marginLeft: '8px',
                      padding: 0,
                      background: 'white',
                      color: '#999',
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '18px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Вопрос по заказу"
                  >
                    <FieldIcon name="chat" size={17} />
                  </button>
                )}
              </div>

              {order.payment_status === 'pending' && (
                <button
                  onClick={(e) => handlePay(order, e)}
                  disabled={payingOrderId === order.id}
                  style={{
                    marginTop: '12px',
                    width: '100%',
                    padding: '12px',
                    background: payingOrderId === order.id ? '#CCC' : '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: payingOrderId === order.id ? 'not-allowed' : 'pointer',
                  }}
                >
                  {payingOrderId === order.id ? 'Загружаем…' : (
                    <>Оплатить<PaymentTimer generatedAt={order.payment_url_generated_at || order.order_date} /></>
                  )}
                </button>
              )}
            </div>
          );
          }
        })}
      </div>

      {showMaxSupportPopup && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20,
        }} onClick={() => setShowMaxSupportPopup(false)}>
          <div style={{
            background: 'white', borderRadius: 16, padding: '20px 24px',
            maxWidth: 320, width: '100%', textAlign: 'center',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: 'var(--text-secondary)' }}><FieldIcon name="chat" size={28} /></div>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#333' }}>Поддержка</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666', lineHeight: 1.5 }}>
              Сформулируйте свой вопрос, а также укажите номер телефона и номер заказа, если он у вас есть — мы обязательно поможем.
            </p>
            {getPlatform() === 'max' ? (
              <button
                onClick={() => { setShowMaxSupportPopup(false); closeApp(); }}
                style={{
                  width: '100%', padding: 14, background: '#4CAF50', color: 'white',
                  border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                  marginBottom: 8,
                }}
              >Открыть чат</button>
            ) : BRAND.supportUrl ? (
              // openPlatformLink, а НЕ <a href target="_blank">: обычная ссылка
              // уходит в window.open, Telegram её перехватывает и просто
              // закрывает мини-апп — юзер оказывается в чате с ботом, откуда
              // приложение открывал, и выглядит это как «поддержка не работает».
              // webapp.openTelegramLink переводит именно в чат поддержки.
              // Остальные три точки входа (BotMenuPage, DeliveryCostDisplay,
              // SubscriptionCheckout) так и делали — этот попап отставал.
              <button
                type="button"
                onClick={() => { setShowMaxSupportPopup(false); openPlatformLink(BRAND.supportUrl); }}
                style={{
                  display: 'block', textAlign: 'center',
                  width: '100%', padding: 14, background: '#4CAF50', color: 'white',
                  border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600,
                  cursor: 'pointer', marginBottom: 8, boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
              >Открыть чат</button>
            ) : null}
            <button
              onClick={() => setShowMaxSupportPopup(false)}
              style={{
                width: '100%', padding: 12, background: 'none', color: '#999',
                border: 'none', fontSize: 14, cursor: 'pointer',
              }}
            >Закрыть</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
